// @flow
import React, {useCallback, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {Waypoint} from '@homeaway/react-unveil';
import {ButtonIcon, ButtonLink} from '@homeaway/react-buttons';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import PropertyCarousel from '../PropertyCarousel/PropertyCarousel';
import {trigger as edapTrigger} from '../../utils/edapWrapper';
import {
    triggerPresentedSerpAmenityCarouselEvent,
    triggerLeadCarouselMabPresentedEvent,
    triggerSelectedSerpAmenityCarouselCardEvent,
    triggerPresentedSerpAmenityCarouselCardEvent
} from '../../utils/collectionEdapUtils';
import {DESKTOP_BREAKPOINTS, MOBILE_BREAKPOINTS} from './breakpoints';

import './AmenityCarousel.less';

type Props = {
    data: any,
    title: string,
    isMobile: boolean,
    seeMoreUrl: string,
    position: string
};

const getIndex = ({cardIndex, cardCount, cardsTotal, next}) => {
    const changeAmount = cardCount;
    return next
        ? Math.min(cardsTotal - cardCount, cardIndex + changeAmount)
        : Math.max(0, cardIndex - changeAmount);
};

const AmenityCarousel = ({
    isMobile,
    seeMoreUrl,
    title,
    data,
    position
}: Props) => {
    const listings = [
        ...data.popularAmenitySearch.searchResult.listings,
        {seeAllCard: true, listingId: 'see-all-card-id'}
    ];
    const [waypointEntered, setWaypointEntered] = useState(false);
    const breakpoints = isMobile ? MOBILE_BREAKPOINTS : DESKTOP_BREAKPOINTS;
    const [cardIndex, setCardIndex] = useState(0);
    const [cardCount, setCardCount] = useState(1);
    const [cardsTotal, setCardsTotal] = useState(listings.length);
    const [showControls, setShowControls] = useState(false);

    const key = data.popularAmenitySearch.searchResult.listings.reduce(
        (acc, value) => {
            return acc + value.listingId;
        },
        ''
    );

    const onNextClick = () => {
        setCardIndex(
            getIndex({
                cardIndex,
                cardCount,
                cardsTotal,
                next: true
            })
        );
        edapTrigger('ux.cards_visible.adjusted', {
            action_location: 'amenity-recommendation-module',
            event_action: 'next_arrow_clicked'
        });
        edapTrigger('nav.next.click', {
            actionlocation: 'amenity-recommendation-module'
        });
    };
    const onPreviousClick = () => {
        setCardIndex(
            getIndex({
                cardIndex,
                cardCount,
                cardsTotal,
                next: false
            })
        );
        edapTrigger('ux.cards_visible.adjusted', {
            action_location: 'amenity-recommendation-module',
            event_action: 'previous_arrow_clicked'
        });
        edapTrigger('nav.previous.click', {
            actionlocation: 'amenity-recommendation-module'
        });
    };

    /* istanbul ignore next */
    const onCarouselResize = ({
        numberCardsVisible,
        numberCardsTotal,
        needsControls
    }) => {
        setCardCount(numberCardsVisible);
        setCardsTotal(numberCardsTotal);
        setShowControls(needsControls);
    };

    const onSeeAllButtonClick = () => {
        edapTrigger('generic.event', {
            eventcategory: 'serp page',
            eventaction: 'see.more.amenitycarousel.link.click'
        });
    };

    const handleWaypointEnter = useCallback(() => {
        if (!waypointEntered) {
            setWaypointEntered(true);
            triggerLeadCarouselMabPresentedEvent();
            triggerPresentedSerpAmenityCarouselEvent(position);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        data.popularAmenitySearch.searchResult.listings,
        position,
        waypointEntered
    ]);

    return (
        <Waypoint onEnter={handleWaypointEnter} useWindowAsScrollParent>
            <div className={`AmenityCarousel AmenityCarousel--${position}`}>
                <div className="PropertyCarousel">
                    <div className="PropertyCarousel__header">
                        <div className="PropertyCarousel__header-content">
                            <h2>{title}</h2>
                            <ButtonLink
                                className="PropertyCarousel__header-link"
                                label={
                                    <FormattedMessage
                                        id="seeAll"
                                        description="see all label"
                                        defaultMessage="See all"
                                    />
                                }
                                href={seeMoreUrl}
                                onClick={onSeeAllButtonClick}
                            />
                        </div>
                        {!isMobile && (
                            <div className="PropertyCarousel__header-controls">
                                <ButtonIcon
                                    label="Previous"
                                    svgMarkup={CHEVRON_LEFT__16}
                                    circle
                                    size="xs"
                                    onClick={onPreviousClick}
                                    disabled={cardIndex === 0}
                                />
                                <ButtonIcon
                                    label="Next"
                                    svgMarkup={CHEVRON_RIGHT__16}
                                    circle
                                    size="xs"
                                    onClick={onNextClick}
                                    disabled={
                                        cardIndex === cardsTotal - cardCount ||
                                        !showControls
                                    }
                                />
                            </div>
                        )}
                    </div>
                    <PropertyCarousel
                        isMobile={isMobile}
                        cardIndex={cardIndex}
                        breakpoints={breakpoints}
                        listings={listings}
                        seeMoreUrl={seeMoreUrl}
                        onCarouselResize={onCarouselResize}
                        onCardClick={
                            triggerSelectedSerpAmenityCarouselCardEvent
                        }
                        onCardEnter={
                            triggerPresentedSerpAmenityCarouselCardEvent
                        }
                        title={''}
                        withImagesCarousel
                        withTripboardButton
                        key={key}
                    />
                </div>
            </div>
        </Waypoint>
    );
};

export default AmenityCarousel;
